import { z } from "zod";

export const RfiStatusSchema = z.enum(["OPEN", "ANSWERED", "CLOSED", "VOID"]);
export const RfiPrioritySchema = z.enum(["LOW", "NORMAL", "HIGH", "URGENT"]);

export const RfiSchema = z.object({
  id: z.string(),
  title: z.string(),
  question: z.string(),
  status: RfiStatusSchema,
  priority: RfiPrioritySchema,
  portalCompanyId: z.string().nullable().optional(),
  companyName: z.string().nullable().optional(),
  raisedByName: z.string().nullable().optional(),
  raisedOnBehalfOfCompany: z.boolean().default(false),
  dueAt: z.string().datetime().nullable().optional(),
  answeredAt: z.string().datetime().nullable().optional(),
  closedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  commentCount: z.number().int().min(0),
});

export type Rfi = z.infer<typeof RfiSchema>;

export const RfiCommentSchema = z.object({
  id: z.string(),
  body: z.string(),
  authorName: z.string().nullable(),
  authorType: z.enum(["INTERNAL", "CONTRACTOR"]).nullable(),
  createdAt: z.string().datetime(),
});

export type RfiComment = z.infer<typeof RfiCommentSchema>;

export const RfiAttachmentSchema = z.object({
  id: z.string(),
  fileUrl: z.string().url(),
  fileName: z.string().nullable(),
  contentType: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export type RfiAttachment = z.infer<typeof RfiAttachmentSchema>;

export const RfiDetailSchema = RfiSchema.extend({
  comments: z.array(RfiCommentSchema),
  attachments: z.array(RfiAttachmentSchema),
});

// ─── #2411 server-boundary variants ─────────────────────────────────────────
// The mobile app parses the permissive bases above; the serialisers in
// `lib/site-access/rfis.ts` parse through these Strict forms.

export const RfiStrict = RfiSchema.strict();
export const RfiCommentStrict = RfiCommentSchema.strict();
export const RfiAttachmentStrict = RfiAttachmentSchema.strict();

export const RfiDetailStrict = RfiDetailSchema.extend({
  comments: z.array(RfiCommentStrict),
  attachments: z.array(RfiAttachmentStrict),
}).strict();

export type RfiDetail = z.infer<typeof RfiDetailSchema>;

export const RfisResponse = z
  .object({
    rfis: z.array(RfiSchema),
  })
  .strict();

export type RfisResponse = z.infer<typeof RfisResponse>;

export const RfiResponse = z
  .object({
    rfi: RfiDetailSchema,
  })
  .strict();

export const RfisResponseStrict = z
  .object({ rfis: z.array(RfiStrict) })
  .strict();

export const RfiResponseStrict = z
  .object({ rfi: RfiDetailStrict })
  .strict();

export type RfiResponse = z.infer<typeof RfiResponse>;
